import type { EChartsOption } from 'echarts'
import { inject, ref, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { Aggregation, IWidget, WidgetType } from '@/interfaces/components/dashboard/widget'
import type { ISensor } from '@/interfaces/entities/sensor'
import { useBarChartWidget, BAR_CHART_ACCENT_COLOR } from './widgets/useBarChartWidget'
import { usePieChartWidget, PIE_CHART_ACCENT_COLOR } from './widgets/usePieChartWidget'
import { useGaugeWidget, GAUGE_ACCENT_COLOR } from './widgets/useGaugeWidget'
import { useIndicatorWidget, INDICATOR_ACCENT_COLOR } from './widgets/useIndicatorWidget'
import { STATUS_PANEL_ACCENT_COLOR } from './widgets/useStatusPanelWidget'
import { useStatusPanelWidget } from '@/composables/widgets/useStatusPanelWidget'
import { SensorType, getSensorTypeBorderColor } from '@/interfaces/enums'
import { useGroupStore } from '@/store/group'
import { getWidgetSensorId, getWidgetGroupId, isGroupWidget } from '@/utils/dashboard/widget-source'
import { useI18n } from '@/composable/useI18n'

export const TIME_SERIES_ACCENT_COLOR = '#123ADD'
export const OPEN_ALARMS_ACCENT_COLOR = '#DC2626'

export type WidgetDensity = 'comfortable' | 'compact' | 'dense'

export function getWidgetDensity(widget: IWidget): WidgetDensity {
  if (widget.w >= 3) return 'comfortable'
  if (widget.w === 2) return 'compact'
  return 'dense'
}

const ACCENT_COLOR_MAP: Record<WidgetType, string> = {
  'bar-chart': BAR_CHART_ACCENT_COLOR,
  'pie-chart': PIE_CHART_ACCENT_COLOR,
  'gauge': GAUGE_ACCENT_COLOR,
  'indicator': INDICATOR_ACCENT_COLOR,
  'time-series': TIME_SERIES_ACCENT_COLOR,
  'status-panel': STATUS_PANEL_ACCENT_COLOR,
  'open-alarms': OPEN_ALARMS_ACCENT_COLOR,
  'totalizers': INDICATOR_ACCENT_COLOR,
}

export function useCustomDashboardWidget(sensorsRef?: Ref<ISensor[]>) {
  const { t } = useI18n()
  const injectedSensors = inject<Ref<ISensor[]>>('dashboardSensors', ref([]))
  const sensors = sensorsRef ?? injectedSensors
  const groupStore = useGroupStore()
  const { listGroups } = storeToRefs(groupStore)

  const barChart = useBarChartWidget()
  const pieChart = usePieChartWidget()
  const gauge = useGaugeWidget()
  const indicator = useIndicatorWidget()
  const { getDisplay: getStatusPanelDisplay } = useStatusPanelWidget()

  const getWidgetAccentColor = (type: WidgetType): string =>
    ACCENT_COLOR_MAP[type] ?? '#E2E8F0'

  const getChartOption = (widget: IWidget): EChartsOption => {
    switch (widget.widget_type) {
      case 'bar-chart':
        return barChart.getOption(widget, '15m')
      case 'pie-chart':
        return pieChart.getOption(widget)
      case 'gauge':
        return gauge.getOption(widget)
      default:
        return {}
    }
  }

  const getAggregationLabel = (widget: IWidget): string => {
    const aggregation = widget.config.aggregation || 'mean'

    const aggMap: Record<Aggregation, string> = {
      mean: 'Média',
      median: 'Mediana',
      last: 'Último',
      sum: 'Soma',
      max: 'Máximo',
      min: 'Mínimo',
    }

    return aggMap[aggregation] || ''
  }

  const getIndicatorValue = (widget: IWidget): string => indicator.getValue(widget)

  const getIndicatorUnit = (widget: IWidget): string => indicator.getUnit(widget)

  const getWidgetBorderColor = (widget: IWidget): string => {
    if (widget.widget_type === 'gauge') {
      return gauge.getAccentColor(widget)
    }
    return getWidgetAccentColor(widget.widget_type)
  }

  const getSensorName = (widget: IWidget): string => {
    if (isGroupWidget(widget)) {
      const groupId = getWidgetGroupId(widget)
      const group = listGroups.value.find((g) => String(g.id) === groupId)
      return group?.name || '-'
    }
    const sensorId = getWidgetSensorId(widget)
    const primary = sensors.value.find((s) => String(s.id) === sensorId)
    if (!primary) return '-'

    const secondaryId = widget.config.sensor_ids[1]
    if (!secondaryId) return primary.name

    const secondary = sensors.value.find((s) => String(s.id) === secondaryId)
    return secondary ? `${primary.name} + ${secondary.name}` : primary.name
  }

  const getSensorForWidget = (widget: IWidget): ISensor | undefined => {
    const sensorId = getWidgetSensorId(widget)
    if (!sensorId) return undefined
    return sensors.value.find((s) => String(s.id) === sensorId)
  }

  const getSecondarySensorForWidget = (widget: IWidget): ISensor | undefined => {
    const secondaryId = widget.config.sensor_ids[1]
    if (!secondaryId) return undefined
    return sensors.value.find((s) => String(s.id) === secondaryId)
  }

  const getWidgetGridConstraints = (widget: IWidget): { minW: number; minH: number } => {
    if (widget.widget_type === 'time-series') {
      return { minW: 4, minH: 5 }
    }
    if (widget.widget_type === 'status-panel') {
      return { minW: 1, minH: 2 }
    }
    if (widget.widget_type === 'gauge' || widget.widget_type === 'indicator') {
      return { minW: 1, minH: 3 }
    }
    if (widget.widget_type === 'totalizers') {
      return { minW: 1, minH: 4 }
    }
    if (widget.widget_type === 'pie-chart') {
      return { minW: 3, minH: 5 }
    }
    if (widget.widget_type === 'bar-chart') {
      return { minW: 4, minH: 5 }
    }
    if (widget.widget_type === 'open-alarms') {
      return { minW: 3, minH: 5 }
    }
    return { minW: 2, minH: 4 }
  }


  const getStatusPanelBorderColor = (widget: IWidget): string => {
    return getStatusPanelDisplay(widget).accentColor
  }

  const getTimeSeriesBorderColor = (widget: IWidget): string => {
    const sensor = getSensorForWidget(widget)
    if (!sensor?.sensor_type) {
      return getWidgetAccentColor('time-series')
    }
    return getSensorTypeBorderColor(sensor.sensor_type as SensorType)
  }

  const getAggregationBadgeLabel = (widget: IWidget): string => {
    const aggregation = widget.config.aggregation || 'mean'
    return t(`custom_dashboards.view.indicator.aggregations.${aggregation}`)
  }

  return {
    getWidgetAccentColor,
    getWidgetBorderColor,
    getChartOption,
    getIndicatorValue,
    getIndicatorUnit,
    getAggregationLabel,
    getSensorName,
    getSensorForWidget,
    getSecondarySensorForWidget,
    getWidgetGridConstraints,
    getWidgetDensity,
    getStatusPanelBorderColor,
    getTimeSeriesBorderColor,
    getAggregationBadgeLabel,
  }
}
